// ─── Central Configuration ─────────────────────────────────────────
// ALL dynamic data is routed through this file.
// Hardcoding values in React components is strictly prohibited.

export interface ServiceConfig {
  id: string;
  title: string;
  description: string;
  icon: string;
  price: string;
  priceNote: string;
  features: string[];
}

export interface CompanyConfig {
  name: string;
  domain: string;
  url: string;
  email: string;
  foundedYear: number;
  tagline: string;
  description: string;
  social: {
    linkedin: string;
    github: string;
    twitter: string;
  };
}

export interface MailerConfig {
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    auth: {
      user: string;
      pass: string;
    };
    from: string;
  };
  contactForm: {
    recipientEmail: string;
    fallbackApiUrl: string;
    fallbackApiKey: string;
    fallbackProvider: string;
  };
}

export interface MetaConfig {
  title: string;
  description: string;
  keywords: string;
}

export interface SiteConfig {
  company: CompanyConfig;
  services: ServiceConfig[];
  meta: MetaConfig;
  mailer: MailerConfig;
}

const config: SiteConfig = {
  company: {
    name: "Transfive Technet",
    domain: "t-5.net",
    url: "https://t-5.net",
    email: "contact@t-5.net",
    foundedYear: 2012,
    tagline: "Engineering Digital Excellence Since 2012",
    description:
      "We are a premium software development agency specializing in iOS app development, game development, Steam publishing, workflow automation, AI integration, web development, REST API development, and Unity3D plugin development.",
    social: {
      linkedin: "",
      github: "https://github.com/shubhank008",
      twitter: "",
    },
  },

  services: [
    {
      id: "ios-dev",
      title: "iOS App Development",
      description:
        "Native and cross-platform iOS applications built with Swift, SwiftUI, and Objective-C. From concept to App Store deployment.",
      icon: "smartphone",
      price: "Starting from $2,000",
      priceNote: "Based on project scope and complexity",
      features: [
        "Native Swift & SwiftUI development",
        "Cross-platform with React Native",
        "App Store submission & optimization",
        "Push notifications & in-app purchases",
        "Ongoing maintenance & support",
      ],
    },
    {
      id: "game-dev",
      title: "Game Development",
      description:
        "Full-cycle game development using Unity3D and Unreal Engine. From prototyping to polished releases on multiple platforms.",
      icon: "gamepad-2",
      price: "Starting from $3,000",
      priceNote: "Depends on game scope and platforms",
      features: [
        "Unity3D & Unreal Engine development",
        "2D and 3D game design",
        "Multiplayer & networking",
        "Cross-platform deployment",
        "Game optimization & testing",
      ],
    },
    {
      id: "steam-publishing",
      title: "Steam Publishing",
      description:
        "End-to-end Steam publishing services including Steamworks integration, store page setup, and launch strategy.",
      icon: "rocket",
      price: "Starting from $1,500",
      priceNote: "Includes Steamworks setup and integration",
      features: [
        "Steamworks SDK integration",
        "Store page creation & optimization",
        "Achievement & leaderboard setup",
        "Workshop & DLC configuration",
        "Launch strategy & marketing support",
      ],
    },
    {
      id: "workflow-automation",
      title: "Workflow Automation",
      description:
        "Streamline your business processes with custom automation solutions. Reduce manual work and increase efficiency.",
      icon: "workflow",
      price: "Starting from $1,000",
      priceNote: "Per automation workflow",
      features: [
        "Business process analysis",
        "Custom automation scripts",
        "Third-party integrations",
        "CI/CD pipeline setup",
        "Monitoring & alerting",
      ],
    },
    {
      id: "ai-integration",
      title: "AI Integration",
      description:
        "Integrate cutting-edge AI capabilities into your applications. LLMs, computer vision, NLP, and custom ML models.",
      icon: "brain",
      price: "Starting from $2,500",
      priceNote: "Based on AI complexity and data requirements",
      features: [
        "LLM & ChatGPT integration",
        "Computer vision solutions",
        "Natural language processing",
        "Custom ML model development",
        "AI-powered automation",
      ],
    },
    {
      id: "web-dev",
      title: "Web Development",
      description:
        "Modern, responsive web applications built with the latest technologies. From landing pages to complex web platforms.",
      icon: "globe",
      price: "Starting from $1,500",
      priceNote: "Based on project requirements",
      features: [
        "React, Vue, Angular frontends",
        "Node.js & PHP backends",
        "Responsive & mobile-first design",
        "SEO optimization",
        "Performance & security",
      ],
    },
    {
      id: "rest-api",
      title: "REST API Development",
      description:
        "Scalable and well-documented RESTful APIs. Built with industry best practices for security, performance, and maintainability.",
      icon: "server",
      price: "Starting from $1,200",
      priceNote: "Based on endpoint complexity",
      features: [
        "RESTful API design & architecture",
        "Authentication & authorization",
        "Database design & optimization",
        "API documentation (Swagger/OpenAPI)",
        "Rate limiting & caching",
      ],
    },
    {
      id: "unity-plugins",
      title: "Unity3D Plugin/Addon Dev",
      description:
        "Custom Unity3D plugins, editor tools, and addons. Extend Unity's capabilities with tailored solutions.",
      icon: "puzzle",
      price: "Starting from $1,500",
      priceNote: "Based on plugin complexity",
      features: [
        "Custom editor tools & windows",
        "Runtime plugins & systems",
        "Asset Store-ready packages",
        "Third-party SDK integration",
        "Documentation & support",
      ],
    },
    {
      id: "consultancy",
      title: "Technical Consultancy",
      description:
        "Expert technical consultation for your projects. Architecture reviews, technology selection, and strategic guidance.",
      icon: "message-circle",
      price: "$30/hour",
      priceNote: "Billed hourly, minimum 1 hour",
      features: [
        "Architecture & design review",
        "Technology stack selection",
        "Code review & best practices",
        "Performance optimization",
        "Security assessment",
      ],
    },
  ],

  meta: {
    title: "Transfive Technet | Software Development Services",
    description:
      "Professional software development services since 2012. iOS apps, game development, Steam publishing, AI integration, web development, and more.",
    keywords:
      "software development, iOS development, game development, Unity3D, Steam publishing, AI integration, web development, REST API, workflow automation",
  },

  // Note: Mailer env vars are referenced here for type documentation.
  // Actual SMTP credentials are configured server-side in config.json.
  mailer: {
    smtp: {
      host: process.env.REACT_APP_SMTP_HOST || "",
      port: Number(process.env.REACT_APP_SMTP_PORT) || 587,
      secure: process.env.REACT_APP_SMTP_SECURE === "true",
      auth: {
        user: process.env.REACT_APP_SMTP_USER || "",
        pass: process.env.REACT_APP_SMTP_PASS || "",
      },
      from:
        process.env.REACT_APP_SMTP_FROM ||
        "Transfive Technet <contact@t-5.net>",
    },
    contactForm: {
      recipientEmail:
        process.env.REACT_APP_CONTACT_EMAIL || "contact@t-5.net",
      fallbackApiUrl:
        process.env.REACT_APP_FALLBACK_API_URL ||
        "https://api.emailservice.com/send",
      fallbackApiKey: process.env.REACT_APP_FALLBACK_API_KEY || "",
      fallbackProvider:
        process.env.REACT_APP_FALLBACK_PROVIDER || "sendgrid",
    },
  },
};

export default config;
